import { Camera, ChevronDown, MapPin, Mic, Search, Sparkles } from "lucide-react";
import { GradientText, useAccent } from "@/ui";
import {
  AD_RESULTS,
  LOCAL_STORES,
  PAA_ITEMS,
  RELATED_SEARCHES,
  SHOPPING_PRODUCTS,
  SITELINKS,
} from "./serpData";

/** Google dark-mode link blue. */
const LINK = "#8ab4f8";

/* ------------------------------------------------------------------ helpers */

function Stars({ rating }: { rating: number }) {
  return (
    <span className="relative inline-block text-xl leading-none">
      <span className="text-white/20">★★★★★</span>
      <span
        className="absolute inset-y-0 left-0 overflow-hidden whitespace-nowrap text-accent-amber"
        style={{ width: `${(rating / 5) * 100}%` }}
      >
        ★★★★★
      </span>
    </span>
  );
}

/** CSS-drawn running-shoe silhouette (side profile) — no external images. */
function ShoeArt({ tone }: { tone: string }) {
  return (
    <div className="flex h-24 items-end justify-center rounded-lg bg-white/5 pb-3">
      <div className="relative h-14 w-28">
        {/* sole */}
        <div className="absolute inset-x-0 bottom-0 h-3.5 rounded-full bg-white/55" />
        {/* heel counter */}
        <div className="absolute bottom-3 left-0 h-10 w-10 rounded-tl-[1.6rem] rounded-tr-sm bg-linear-to-b from-white/35 to-white/15" />
        {/* midfoot + toe box */}
        <div className="absolute bottom-3 left-8 right-0 h-7 rounded-tr-[1.4rem] bg-linear-to-b from-white/30 to-white/12" />
        {/* collar notch */}
        <div className="absolute left-2 top-1 h-2.5 w-4 rounded-full bg-[#292a2d]" />
        {/* laces */}
        <div className="absolute bottom-7 left-11 h-1 w-4 -rotate-[24deg] rounded-full bg-[#202124]/80" />
        <div className="absolute bottom-6 left-14 h-1 w-4 -rotate-[24deg] rounded-full bg-[#202124]/80" />
        <div className="absolute bottom-5 left-[4.25rem] h-1 w-4 -rotate-[24deg] rounded-full bg-[#202124]/80" />
        {/* accent stripe */}
        <div
          className="absolute bottom-4 left-1.5 h-1.5 w-11 rounded-full"
          style={{ backgroundColor: tone }}
        />
      </div>
    </div>
  );
}

function Favicon({ className = "bg-linear-to-br from-white/35 to-white/10" }: { className?: string }) {
  return <span className={`h-8 w-8 shrink-0 rounded-full ${className}`} />;
}

/* ------------------------------------------------------------- R1 The query */

export function SearchHeader() {
  return (
    <div>
      <div className="flex items-center gap-6">
        <span className="select-none font-display text-3xl font-semibold tracking-tight text-white/90">
          Google
        </span>
        <div className="flex min-w-0 max-w-2xl flex-1 items-center gap-4 rounded-full bg-[#303134] px-6 py-3">
          <span className="truncate text-xl text-white">best running shoes for flat feet</span>
          <span className="ml-auto flex shrink-0 items-center gap-4">
            <Mic className="h-6 w-6" style={{ color: LINK }} />
            <Camera className="h-6 w-6" style={{ color: LINK }} />
            <Search className="h-6 w-6" style={{ color: LINK }} />
          </span>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-8 pl-1 text-xl text-white/70">
        <span className="border-b-2 pb-1 font-medium" style={{ color: LINK, borderColor: LINK }}>
          All
        </span>
        <span>Shopping</span>
        <span>Images</span>
        <span>Videos</span>
        <span>News</span>
      </div>
    </div>
  );
}

/* --------------------------------------------------- R2 Sponsored results */

export function SponsoredAds() {
  return (
    <div className="flex flex-col gap-5">
      <p className="text-xl font-semibold text-white/90">Sponsored</p>
      {AD_RESULTS.map((ad) => (
        <div key={ad.title}>
          <div className="flex items-center gap-3">
            <Favicon />
            <div className="min-w-0 leading-tight">
              <p className="text-xl text-white/90">{ad.advertiser}</p>
              <p className="truncate text-xl text-white/70">{ad.url}</p>
            </div>
          </div>
          <p className="mt-1.5 text-2xl font-medium" style={{ color: LINK }}>
            {ad.title}
          </p>
          <p className="mt-1 max-w-3xl text-xl leading-normal text-white/70">{ad.desc}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------------------------------------------------- R3 Shopping results */

export function ShoppingCarousel() {
  return (
    <div>
      <p className="mb-4 text-2xl text-white/90">Popular products</p>
      <div className="flex gap-3">
        {SHOPPING_PRODUCTS.map((p) => (
          <div
            key={p.name}
            className="w-44 shrink-0 rounded-xl border border-[#3c4043] bg-[#292a2d] p-3"
          >
            <ShoeArt tone={p.tone} />
            <p className="mt-3 truncate text-xl text-white/90">{p.name}</p>
            <p className="mt-0.5 text-xl font-semibold text-white">{p.price}</p>
            <div className="mt-0.5 flex items-center gap-2">
              <Stars rating={p.rating} />
              <span className="text-xl text-white/70">{p.rating.toFixed(1)}</span>
            </div>
            <p
              className={`mt-0.5 truncate text-xl ${
                p.retailer === "stride.example"
                  ? "font-semibold text-white"
                  : "text-white/70"
              }`}
            >
              {p.retailer}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------- R4 AI Overview */

export function AiOverviewBlock() {
  const [from, to] = useAccent();
  return (
    <div
      className="rounded-2xl p-px"
      style={{ background: `linear-gradient(120deg, ${from}66, ${to}66)` }}
    >
      <div className="rounded-[calc(1rem-1px)] bg-[#26282e] p-6">
        <div className="flex items-center gap-3">
          <Sparkles className="h-7 w-7" style={{ color: from }} />
          <GradientText className="text-2xl font-medium">AI Overview</GradientText>
        </div>
        <p className="mt-3 max-w-4xl text-xl leading-relaxed text-white/85">
          Runners with flat feet often do best in stability shoes with structured arch
          support and a firm midsole. Fit matters more than any single model, so try
          shoes with the socks and distances you actually run.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          {["strideguide.example", "runlab.example", "feetfirst.example"].map((s) => (
            <span
              key={s}
              className="flex items-center gap-2.5 rounded-full bg-[#303134] px-4 py-1.5 text-xl text-white/70"
            >
              <span className="h-4 w-4 rounded-full bg-linear-to-br from-white/40 to-white/15" />
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------- R5 The #1 organic result */

export function OrganicHero() {
  return (
    <div>
      <div className="flex items-center gap-3">
        <Favicon className="bg-linear-to-br from-accent-cyan to-accent-indigo" />
        <div className="min-w-0 leading-tight">
          <p className="text-xl text-white/90">StrideLab</p>
          <p className="truncate text-xl text-white/70">
            stridelab.example › guides › flat-feet-running-shoes
          </p>
        </div>
      </div>
      <p className="mt-2 text-2xl font-medium" style={{ color: LINK }}>
        Best Running Shoes for Flat Feet (2026 Guide)
      </p>
      <p className="mt-1.5 max-w-3xl text-xl leading-normal text-white/70">
        We tested 24 stability and motion-control shoes with flat-footed runners. Our
        top picks by budget, plus how to check your arch type at home.
      </p>
      <div className="mt-4 grid max-w-2xl grid-cols-2 gap-x-12 gap-y-2.5">
        {SITELINKS.map((s) => (
          <span key={s} className="text-xl" style={{ color: LINK }}>
            {s}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- R6 Rich result */

export function RichResult() {
  return (
    <div>
      <div className="flex items-center gap-3">
        <Favicon className="bg-linear-to-br from-accent-emerald to-accent-cyan" />
        <div className="min-w-0 leading-tight">
          <p className="text-xl text-white/90">ShoeCritic</p>
          <p className="truncate text-xl text-white/70">
            shoecritic.example › reviews › flat-feet-running-shoes
          </p>
        </div>
      </div>
      <p className="mt-2 text-2xl font-medium" style={{ color: LINK }}>
        Flat Feet Running Shoes — Tested &amp; Reviewed
      </p>
      <div className="mt-1.5 flex items-center gap-3 text-xl text-white/70">
        <Stars rating={4.6} />
        <span>4.6 · 890 reviews · $89–$189</span>
      </div>
    </div>
  );
}

/* ------------------------------------------------------ R7 People Also Ask */

export function PeopleAlsoAsk() {
  return (
    <div>
      <p className="mb-3 text-2xl text-white/90">People also ask</p>
      <div className="divide-y divide-[#3c4043] rounded-xl border border-[#3c4043]">
        {PAA_ITEMS.map((item) => (
          <div key={item.q} className="px-5 py-4">
            <div className="flex items-center justify-between gap-4">
              <p className="text-xl text-white/90">{item.q}</p>
              <ChevronDown
                className={`h-6 w-6 shrink-0 text-white/50 transition ${
                  item.answer ? "rotate-180" : ""
                }`}
              />
            </div>
            {item.answer && (
              <div className="mt-3">
                <p className="max-w-3xl text-xl leading-normal text-white/70">
                  {item.answer}
                </p>
                {item.source && (
                  <p className="mt-2 truncate text-xl" style={{ color: LINK }}>
                    {item.source}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ R8 Local pack */

export function LocalPack() {
  return (
    <div>
      <p className="mb-3 text-2xl text-white/90">Places</p>
      {/* CSS-drawn map placeholder */}
      <div className="relative mb-4 h-24 overflow-hidden rounded-xl bg-[#1d2a30]">
        <div className="absolute -left-6 top-8 h-1.5 w-[120%] -rotate-3 bg-white/10" />
        <div className="absolute -top-4 left-1/4 h-[150%] w-1.5 rotate-12 bg-white/10" />
        <div className="absolute -top-4 left-2/3 h-[150%] w-1 -rotate-6 bg-white/[0.07]" />
        <div className="absolute -left-6 bottom-4 h-1 w-[120%] rotate-2 bg-white/[0.07]" />
        <MapPin className="absolute left-[16%] top-4 h-7 w-7 fill-accent-rose/40 text-accent-rose" />
        <MapPin className="absolute left-[48%] top-9 h-7 w-7 fill-accent-rose/40 text-accent-rose" />
        <MapPin className="absolute left-[76%] top-3 h-7 w-7 fill-accent-rose/40 text-accent-rose" />
      </div>
      <div className="space-y-3">
        {LOCAL_STORES.map((store) => (
          <div key={store.name} className="flex items-center gap-4">
            <p className="w-64 truncate text-xl text-white/90">{store.name}</p>
            <Stars rating={store.rating} />
            <span className="text-xl text-white/70">{store.rating.toFixed(1)}</span>
            <span className="text-xl text-white/70">Open until 6pm</span>
            <span className="text-xl font-medium text-accent-emerald">Gait analysis</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xl italic text-white/50">
        appears for local-intent searches like &lsquo;running shoes near me&rsquo;
      </p>
    </div>
  );
}

/* ----------------------------------------------------- R9 Related searches */

export function RelatedSearches() {
  return (
    <div>
      <p className="mb-3 text-2xl text-white/90">Related searches</p>
      <div className="flex flex-wrap gap-3">
        {RELATED_SEARCHES.map((q) => (
          <span
            key={q}
            className="flex items-center gap-2.5 rounded-full bg-[#303134] px-5 py-2.5 text-xl text-white/85"
          >
            <Search className="h-5 w-5 shrink-0 text-white/50" />
            {q}
          </span>
        ))}
      </div>
    </div>
  );
}
